/**
 * Web implementation of the collapsible AccordionField using React Aria.
 *
 * Mirrors the behaviour of the native `accordion-field.tsx`: `forceOpen` keeps
 * the accordion permanently expanded and hides the toggle cue; `highlighted`
 * switches to a bordered, tinted visual style; `defaultOpen` sets the initial
 * expanded state. The open/close state is tracked in local state so the
 * `StatusCue` chevron always reflects the current expansion.
 */
import { ChevronDown } from '@carbon/icons-react';
import { useTheme } from '@repo/theme';
import { cx } from 'class-variance-authority';
import { UtilityText } from 'component/text';
import { useContext } from 'react';
import {
    Button as AriaButton,
    Disclosure as AriaDisclosure,
    DisclosurePanel as AriaDisclosurePanel,
    Heading as AriaHeading,
    type DisclosureProps,
    DisclosureStateContext,
} from 'react-aria-components';
import { t } from 'util/stdtypes';

import styles from './accordion-card.module.css';

export type AccordionCardProps = DisclosureProps & {
    title: React.ReactNode;
    testID?: string;
    isExpanded?: boolean;
    defaultExpanded?: boolean;
    highlighted?: boolean;
    children: React.ReactNode;
    className?: string;
    panelClassName?: string;
    headerClassName?: string;
    onExpandedChange?: (isExpanded: boolean) => void;
    /**
     * Heading level for the header. Default 3 — most disclosures live inside
     * a section.
     */
    headingLevel?: 1 | 2 | 3 | 4 | 5 | 6;
};

function StatusCue({ highlighted }: { highlighted: boolean }) {
    const { isExpanded = false } = useContext(DisclosureStateContext) ?? {};
    const { colors } = useTheme();

    if (highlighted) {
        if (isExpanded) {
            return <UtilityText color={colors.colorTextSecondary} label={t('form.accordion.hide_details', 'Hide details')} underline />;
        }
        return <UtilityText color={colors.colorTextSecondary} label={t('form.accordion.show_details', 'Show details')} underline />;
    }
    // for the chevron flippinng it, is handled by an animation in the css
    return <ChevronDown aria-hidden className={styles.chevron} color={colors.colorTextPrimary} />;
}

export function AccordionCard({
    title,
    headingLevel = 3,
    children,
    className,
    panelClassName,
    headerClassName,
    testID,
    highlighted,
    isExpanded,
    defaultExpanded,
    onExpandedChange,
    ...props
}: AccordionCardProps) {
    return (
        <AriaDisclosure
            className={cx(styles.accordion, className)}
            data-highlighted={highlighted || undefined}
            defaultExpanded={defaultExpanded}
            isExpanded={isExpanded}
            onExpandedChange={onExpandedChange}
            {...props}
        >
            <AriaHeading level={headingLevel}>
                <AriaButton className={cx(styles.header, headerClassName)} data-testid={testID} slot='trigger'>
                    <span className={styles.title}>{title}</span>
                    <StatusCue highlighted={!!highlighted} />
                </AriaButton>
            </AriaHeading>
            {/* The .panelInner wrapper exists so the grid-template-rows 0fr/1fr
                trick has a single grid item to size. */}
            <AriaDisclosurePanel className={cx(styles.panel, panelClassName)}>
                <div className={styles.panelInner}>{children}</div>
            </AriaDisclosurePanel>
        </AriaDisclosure>
    );
}
